import Modal from "components/DesignSystem/Modal/Modal";
import { useModal } from "hooks/useModal";
import { useWindowSize } from "hooks/useWindowSize";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { Button } from "components/DesignSystem/Button/Button";
import { Cross } from "components/icons/Cross";
import { DividerLine } from "components/icons/DividerLine";
import { Padlock } from "components/icons/Padlock";
import { LockedUi } from "components/LockedUi/LockedUi";
import { SelectedFloatingControl } from "components/SelectedFloatingControl/SelectedFloatingControl";
import { WritePermissionKey } from "constants/permissions";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useGetCategoryByIdentifierQuery } from "store/apis/chartOfAccounts";

type LineItemBulkCategoryBarProps = {
  selectedCount: number;
  rootCategoryIdentifiers: string[];
  writePermissionKey: WritePermissionKey;
  isApplying?: boolean;
  onApply: (categoryId: string) => void;
  onCancel: () => void;
};

export const LineItemBulkCategoryBar = ({
  selectedCount,
  rootCategoryIdentifiers,
  writePermissionKey,
  isApplying = false,
  onApply,
  onCancel,
}: LineItemBulkCategoryBarProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { isMobile } = useWindowSize();
  const categoryModal = useModal();
  const entityId = useCurrentEntityId();

  const { data: categoryData } = useGetCategoryByIdentifierQuery(
    { groupId, entityId: entityId!, identifiers: rootCategoryIdentifiers },
    { skip: !groupId || !entityId }
  );

  const rootCategoryId = categoryData
    ?.map((category) => category.uuid)
    .join(",");

  const handleCategoryChange = (value: { value: string } | null) => {
    if (!value) return;

    categoryModal.close();
    onApply(value.value);
  };

  const categorySelector = (
    <CategorySelector
      key={selectedCount}
      withForm={false}
      category={null}
      hiddenCategoryTypes={[]}
      root_category_id={rootCategoryId}
      placeholder="Set category"
      isUpdating={isApplying}
      isDisabled={isApplying}
      hideAction
      outline
      size="small"
      menuPlacement={isMobile ? "bottom" : "top"}
      onChange={handleCategoryChange}
    />
  );

  return (
    <SelectedFloatingControl>
      {selectedCount > 0 && (
        <>
          <span className="t-whitespace-nowrap t-text-subtext t-text-text-60">
            {selectedCount} selected
          </span>
          <span className="t-text-neutral-10 t-h-full t-flex">
            <DividerLine />
          </span>
          <LockedUi
            feature={writePermissionKey}
            blockedUi={
              <div className="t-w-60">
                <Button
                  type="button"
                  customType="secondary"
                  size="small"
                  disabled
                  className="t-w-full"
                >
                  <span className="t-flex t-items-center t-gap-2">
                    <Padlock />
                    Set category
                  </span>
                </Button>
              </div>
            }
          >
            {isMobile ? (
              <Modal.Root
                open={categoryModal.isOpen}
                onOpenChange={categoryModal.toggle}
              >
                <Modal.Trigger asChild>
                  <Button type="button" customType="secondary" size="small">
                    Set category
                  </Button>
                </Modal.Trigger>
                <Modal.Content size="small" useCustomOverlay>
                  <Modal.Header>
                    <Modal.Title>Set category</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-pb-4">{categorySelector}</Modal.Body>
                </Modal.Content>
              </Modal.Root>
            ) : (
              <div className="t-w-60">{categorySelector}</div>
            )}
          </LockedUi>
          <Button
            type="button"
            customType="ghost_icon"
            size="xsmall"
            disabled={isApplying}
            onClick={onCancel}
          >
            <Cross />
          </Button>
        </>
      )}
    </SelectedFloatingControl>
  );
};
